import * as React from 'react';
import type { TreeItem, TreeItemIndex } from 'react-complex-tree';
import type { ITreeNode, SearchableTreeItem, UseComplexTreeSearchResult } from '../types';

const DEFAULT_SEARCH_DELAY = 300;
const MAX_SEARCH_RESULTS_LIMIT = 50;

/**
 * Provides debounced search behavior for the ComplexTree.
 * - Tracks the current search term and exposes input handlers
 * - Expands all ancestor items of items that match the query
 * - Exposes a reusable predicate to check if an item matches a query
 */
export const useComplexTreeSearch = (
	treeItems: Record<string, TreeItem<ITreeNode>>,
	setExpandedNodeIds: (expandedNodeIds: Set<TreeItemIndex>) => void
): UseComplexTreeSearchResult => {
	const [searchTerm, setSearchTerm] = React.useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
	const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const doesItemMatchSearch = React.useCallback((item: TreeItem<ITreeNode>, searchQuery: string): boolean => {
		if (!searchQuery) return false;

		const label = item.data?.label || '';

		return label.trim().toLowerCase().includes(searchQuery.trim().toLowerCase());
	}, []);

	const childToParentMap = React.useMemo(() => {
		const map = new Map<TreeItemIndex, TreeItemIndex>();
		Object.entries(treeItems).forEach(([id, item]) => {
			if (item.children) {
				item.children.forEach(childId => {
					map.set(childId, id);
				});
			}
		});
		return map;
	}, [treeItems]);

	// Pre-compute search index with lowercased labels for faster searching (as we expect treeItems to change much less often than searchQuery)
	const searchableItems: SearchableTreeItem[] = React.useMemo(() => {
		return Object.values(treeItems).map(item => ({
			item,
			searchText: (item.data?.label || '').toLowerCase(),
		}));
	}, [treeItems]);

	const debouncedSearch = React.useMemo(() => {
		return (searchQuery: string) => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				setDebouncedSearchTerm(searchQuery);

				if (!searchQuery) {
					setExpandedNodeIds(new Set());
					return;
				}

				const lowercasedQuery = searchQuery.toLowerCase().trim();
				const itemsToExpand = new Set<TreeItemIndex>();

				const expandAncestorsOfItem = (item: TreeItem<ITreeNode>) => {
					let currentId: TreeItemIndex = item.index;
					let parentId = childToParentMap.get(currentId);

					while (parentId !== undefined) {
						// If parent already expanded, all ancestors are too - early exit from this function call
						if (itemsToExpand.has(parentId)) return;

						itemsToExpand.add(parentId);
						currentId = parentId;
						parentId = childToParentMap.get(currentId);
					}
				};

				// Find matching items and expand their ancestors (limit to first MAX_SEARCH_RESULTS_LIMIT matches)
				let matchCount = 0;
				for (const { item, searchText } of searchableItems) {
					if (matchCount >= MAX_SEARCH_RESULTS_LIMIT) break;

					if (searchText.includes(lowercasedQuery)) {
						matchCount++;
						expandAncestorsOfItem(item);
					}
				}

				setExpandedNodeIds(new Set(itemsToExpand));
			}, DEFAULT_SEARCH_DELAY);
		};
	}, [searchableItems, childToParentMap, setExpandedNodeIds]);

	// Cleanup pending timeout when debouncedSearch changes or on unmount
	React.useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [debouncedSearch]);

	const handleSearch = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			setSearchTerm(value);
			debouncedSearch(value);
		},
		[debouncedSearch]
	);

	const clearSearch = React.useCallback(() => {
		setSearchTerm('');
		setDebouncedSearchTerm('');
		setExpandedNodeIds(new Set());
	}, [setExpandedNodeIds]);

	return {
		searchTerm,
		debouncedSearchTerm,
		handleSearch,
		clearSearch,
		doesItemMatchSearch,
	};
};
